import { FC, useCallback, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { PiCurrencyDollarBold, PiSignOut } from "react-icons/pi";
import { Button, Tooltip } from "rizzui";
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import PageHeader from "../../../layout/shared/page-header";
import CustomSpinner from "../../../loader/CustomSpinner";
import { VehicleService } from "../../../services/vehicles/vehicleService";
import { VehicleApiResponse } from "../../../type/entities/vehicle-type";
import SellVehicleModal from "../../../components/forms/SellVehicleModal";
import { FaMoneyBillTrendUp } from "react-icons/fa6";

const menuItems: MenuItem[] = [
    {
        label: 'Información General',
        path: 'info',
        permission: {
            group: 'vehicle',
            action: 'get',
        },
    },
    {
        label: 'Historial de Repostajes',
        path: 'refueling',
        permission: {
            group: 'vehicle',
            action: 'get',
        },
    },
    {
        label: 'Registro de Mantenimientos',
        path: 'maintenance',
        permission: {
            group: 'vehicle',
            action: 'get',
        },
    }
];

const VehicleProfileLayout: FC = () => {
    const { id } = useParams<{ id: string }>();
    const service = new VehicleService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    
    // Estados para el modal de venta y carga
    const [sellModalOpen, setSellModalOpen] = useState(false);
    const [sellLoading, setSellLoading] = useState(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getVehicleById(id);
        return response.getResponseData() as VehicleApiResponse;
    }, []));
    
    // Funciones para manejar la venta del vehículo
    const handleOpenSellModal = () => {
        setSellModalOpen(true);
    };

    const handleCloseSellModal = () => {
        setSellModalOpen(false);
    };

    const handleSellVehicle = async (saleDate: Date) => {
        if (!data || !id) return;

        try {
            setSellLoading(true);
            const formattedDate = moment(saleDate).format('YYYY-MM-DD');
            const response = await (await service.markAsSold(id, formattedDate)).getResponseData();
            
            if (response.success) {
                setSellLoading(false);
                setSellModalOpen(false);
                refetch(); // Actualizar los datos del vehículo
                toast.success('Vehículo marcado como vendido correctamente');
            } else {
                setSellLoading(false);
                handleErrors(response);
            }
        } catch (error) {
            setSellLoading(false);
            handleErrors(error);
        }
    };
    

    return (
        <div className="space-y-6">
            {data && !loading && (<>
                <PageHeader
                    title={`${data?.plateNumber} - ${data?.brand} ${data?.model}`}
                    breadcrumb={[
                        {
                            href: '/vehicles',
                            name: 'Vehículos',
                        },
                        {
                            name: data?.plateNumber,
                        },
                    ]}
                >
                    <div className="flex items-center gap-3">
                        {userCan('edit', 'vehicle') && data?.status !== 'sold' && (
                            <Tooltip
                                size="sm"
                                content={sellLoading ? 'Procesando venta...' : 'Marcar como vendido'}
                                placement="bottom"
                                color="invert"
                            >
                                <Button
                                    variant="solid"
                                    size="md"
                                    onClick={handleOpenSellModal}
                                    isLoading={sellLoading}
                                    disabled={sellLoading}
                                    className="bg-primary text-white"
                                >
                                    <FaMoneyBillTrendUp className="h-4 w-4 me-2" />
                                    {sellLoading ? 'Vendiendo...' : 'Vender'}
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                </PageHeader>

                <TabNav
                    menuItems={menuItems}
                    data={data}
                />

                <Outlet />
            </>
            )}
            {!data || loading && (
                <div className="flex justify-center items-center h-[calc(100vh-160px)]">
                    <CustomSpinner />
                </div>
            )}
            
            {/* Modal para vender vehículo */}
            <SellVehicleModal
                isOpen={sellModalOpen}
                onClose={handleCloseSellModal}
                onConfirm={handleSellVehicle}
                vehicle={data ?? null}
                isLoading={sellLoading}
            />
        </div>
    );
};

export default VehicleProfileLayout;